import { useState } from 'react'
import type { FormEvent } from 'react'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { useAuth } from '../auth/useAuth'
import { AuthCard } from './AuthCard'
import styles from './LoginPage.module.scss'

function buildRedirectUrl(
  next: string | null,
  cliCallbackUrl: string | null,
  cliState: string | null,
): string {
  if (cliCallbackUrl) {
    const url = new URL('/login', window.location.origin)
    url.searchParams.set('cli_callback_url', cliCallbackUrl)
    if (cliState) url.searchParams.set('cli_state', cliState)
    return url.toString()
  }
  // Only allow same-origin paths starting with "/" — protects against open
  // redirect via a poisoned ?next= query param.
  const safeNext = next && next.startsWith('/') ? next : '/'
  return `${window.location.origin}${safeNext}`
}

export function LoginPage() {
  const { requestMagicLink, linkExpired } = useAuth()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const next = params.get('next')
  const cliCallbackUrl = params.get('cli_callback_url')
  const cliState = params.get('cli_state')
  const [email, setEmail] = useState(params.get('email_hint') ?? '')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [devLink, setDevLink] = useState<string | null>(null)
  const shownMessage =
    message ??
    (linkExpired ? 'Questo link è già stato usato o è scaduto. Richiedi un nuovo link qui sotto.' : null)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    const cleanEmail = email.trim()
    if (!cleanEmail) return
    setStatus('sending')
    setMessage(null)
    setDevLink(null)
    try {
      const payload = await requestMagicLink(
        cleanEmail,
        buildRedirectUrl(next, cliCallbackUrl, cliState),
      )
      setStatus('sent')
      setMessage('Controlla la tua email per il link di accesso.')
      if (payload.login_url) setDevLink(payload.login_url)
    } catch (error: unknown) {
      setStatus('error')
      setMessage(error instanceof Error ? error.message : 'Impossibile richiedere un link di accesso')
    }
  }

  return (
    <AuthCard
      title="Progetto Arca"
      subtitle="Accedi con la tua email aziendale."
      message={shownMessage}
      messageIsError={status === 'error'}
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <label className={styles.label}>
          <span className={styles.labelText}>Email</span>
          <input
            autoComplete="email"
            className={styles.input}
            inputMode="email"
            name="email"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@company.com"
            required
            type="email"
            value={email}
          />
        </label>
        <Button disabled={status === 'sending'} type="submit">
          {status === 'sending' ? 'Invio in corso...' : 'Invia link di accesso'}
        </Button>
      </form>
      {devLink ? (
        <a className={styles.devLink} href={devLink}>
          Apri link locale di accesso
        </a>
      ) : null}
    </AuthCard>
  )
}
